import {
 ReactNode,
 createContext,
 useContext,
 useEffect,
 useState,
} from "react";
import { useNavigate } from "react-router-dom";
import * as Apis from "services/api";

/**
 * Get backend base URL dynamically based on current hostname
 * This ensures subdomain support works correctly
 */
const getBackendBaseUrl = (): string => {
 // If REACT_APP_BACKEND_BASE_URL is explicitly set, use it
 if (process.env.REACT_APP_BACKEND_BASE_URL && process.env.REACT_APP_BACKEND_BASE_URL !== "/") {
  return process.env.REACT_APP_BACKEND_BASE_URL;
 }

 // For localhost subdomain testing, use current hostname with backend port
 if (typeof window !== "undefined") {
  const hostname = window.location.hostname;

  // If using subdomain.localhost, use same hostname with backend port
  if (hostname.includes("localhost") || hostname.includes("local")) {
   return `http://${hostname}:5100`;
  }

  // For production/staging, use same hostname
  return `${window.location.protocol}//${hostname}`;
 }

 // Fallback for SSR or when window is not available
 return "http://localhost:5100";
};

const BACKEND_BASE_URL = getBackendBaseUrl();
export const backendBaseUrl = `${BACKEND_BASE_URL === "/" ? "" : BACKEND_BASE_URL}/api`;

const ApiContext = createContext<{
 maintenanceMode: boolean;
 systemInfo: any;
 [key: string]: any;
}>({} as any);

interface Props {
 children?: ReactNode;
}
export const ApiProvider = ({ children }: Props) => {
 const [maintenanceMode, setMaintenanceMode] = useState(false);
 const [systemInfo, setSystemInfo] = useState({});
 const navigate = useNavigate();

 const getSystemInfo = async () => {
  try {
   const response = await Apis.getSystemInfo();
   setSystemInfo(response?.data);
  } catch (error) {
   setSystemInfo({});
  }
 };
 const getHealthStatus = async () => {
  try {
   const response = await Apis.getHealth();
   setMaintenanceMode(response.status !== 200);
  } catch (error) {
   setMaintenanceMode(true);
  }
 };
 useEffect(() => {
  getHealthStatus();
  getSystemInfo();
 }, []);

 useEffect(() => {
  // Don't redirect to maintenance page if user is on platform admin portal
  const isPlatformRoute = window.location.pathname.startsWith("/platform");
  if (maintenanceMode && !isPlatformRoute) {
   navigate("/maintenance");
  }
 }, [maintenanceMode, navigate]);

 const value = {
  ...Apis,
  maintenanceMode,
  systemInfo,
 };

 return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

// Custom hook to use the context easily
export const useApi = () => {
 return useContext(ApiContext);
};
